import React from 'react';
import { POPULAR_TRANSLATIONS, OTHER_TRANSLATIONS, getTranslationById } from '../utils/translations.js';
import { t } from '../utils/lang.js';

export default function TranslationSelector({ isOpen, onClose, selectedIds, onSelect }) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [draftIds, setDraftIds] = React.useState(selectedIds || []);

  React.useEffect(() => {
    if (!isOpen) return;
    setDraftIds(Array.isArray(selectedIds) ? selectedIds : []);
  }, [isOpen, selectedIds]);

  if (!isOpen) return null;

  const filterTranslations = (translations) => {
    if (!searchTerm) return translations;
    const term = searchTerm.toLowerCase();
    return translations.filter(t =>
      t.name.toLowerCase().includes(term) ||
      t.nativeName.toLowerCase().includes(term) ||
      t.author.toLowerCase().includes(term) ||
      t.lang.toLowerCase().includes(term)
    );
  };

  const filteredPopular = filterTranslations(POPULAR_TRANSLATIONS);
  const filteredOther = filterTranslations(OTHER_TRANSLATIONS);

  const toggleTranslation = (id) => {
    setDraftIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(sid => sid !== id);
      }
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
  };

  return (
    <div className="translation-selector-overlay" onClick={onClose}>
      <div className="translation-selector" onClick={(e) => e.stopPropagation()}>
        <div className="translation-selector__header">
          <h2>{t('choose_translations')}</h2>
          <button className="translation-selector__close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="translation-selector__search">
          <input
            type="text"
            placeholder={t('search_translation')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="translation-selector__hint">
          {t('selected_of_5', { n: draftIds.length })}
        </div>

        <div className="translation-selector__content">
          {filteredPopular.length > 0 && (
            <div className="translation-group">
              <h3 className="translation-group__title">{t('popular')}</h3>
              {filteredPopular.map((translation) => (
                <TranslationItem
                  key={translation.id}
                  translation={translation}
                  isSelected={draftIds.includes(translation.id)}
                  onToggle={() => toggleTranslation(translation.id)}
                />
              ))}
            </div>
          )}

          {filteredOther.length > 0 && (
            <div className="translation-group">
              <h3 className="translation-group__title">{t('other_langs')}</h3>
              {filteredOther.map((translation) => (
                <TranslationItem
                  key={translation.id}
                  translation={translation}
                  isSelected={draftIds.includes(translation.id)}
                  onToggle={() => toggleTranslation(translation.id)}
                />
              ))}
            </div>
          )}

          {filteredPopular.length === 0 && filteredOther.length === 0 && (
            <div className="translation-selector__empty">
              {t('nothing_found')}
            </div>
          )}
        </div>

        <div className="translation-selector__footer">
          <button
            className="btn btn-primary"
            onClick={() => {
              try { onSelect(Array.isArray(draftIds) ? draftIds : []); } catch { }
              onClose();
            }}
          >
            {t('done')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TranslationItem({ translation, isSelected, onToggle }) {
  return (
    <div
      className={`translation-item ${isSelected ? 'selected' : ''}`}
      onClick={onToggle}
    >
      <div className="translation-item__checkbox">
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
        />
      </div>
      <div className="translation-item__content">
        <div className="translation-item__name">
          {translation.nativeName}
        </div>
        <div className="translation-item__author">
          {translation.author}
        </div>
      </div>
      <div className="translation-item__lang">
        {translation.lang.toUpperCase()}
      </div>
    </div>
  );
}
